import { Platform } from 'react-native';
import { create } from 'zustand';

import { loadSetting, saveSetting } from '../../shared/storage/settingsStorage';
import { normalizeQuestionLocale } from '../../shared/utils/questionLocales';

interface LanguageState {
  language: string;
  loading: boolean;
  setLanguage: (lang: string) => void;
  setLanguageLoading: (loading: boolean) => void;
  initializeLanguage: () => Promise<void>;
  detectDeviceLanguage: () => string;
}

export const useLanguageStore = create<LanguageState>((set, get) => ({
  language: 'en', // Default language
  loading: true,
  
  setLanguage: async (lang: string) => {
    const normalizedLang = normalizeQuestionLocale(lang);

    // Update the store immediately
    set({ language: normalizedLang, loading: false });

    // Save to persistent storage
    try {
      await saveSetting('language', normalizedLang);
      console.log(`✅ Language saved to storage: ${normalizedLang}`);
    } catch (error) {
      console.error('❌ Failed to save language to storage:', error);
    }

    // Clear any conflicting landing page selection when user explicitly sets language
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      const landingSelection = localStorage.getItem('user_selected_language');
      if (landingSelection && landingSelection !== normalizedLang) {
        console.log(`🔄 Clearing outdated landing page selection: ${landingSelection}`);
        localStorage.removeItem('user_selected_language');
      }
    }

    // Also save to user profile in database for cross-device sync
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { error } = await supabase
          .from("user_profiles")
          .upsert({
            id: user.id,
            language: normalizedLang
          }, {
            onConflict: 'id'
          });

        if (error) {
          console.warn('⚠️ Failed to save language to user profile:', error);
        } else {
          console.log(`✅ Language saved to user profile: ${normalizedLang}`);
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to update user profile language:', error);
    }
  },
  
  setLanguageLoading: (loading: boolean) => set({ loading }),
  
  // Detect device language and fallback to supported language
  detectDeviceLanguage: () => {
    try {
      let deviceLanguage = 'en';
      
      if (Platform.OS === 'web') {
        // For web, use browser language
        if (typeof window !== 'undefined' && window.navigator) {
          const browserLang = window.navigator.language || window.navigator.languages?.[0];
          if (browserLang) {
            deviceLanguage = browserLang.split('-')[0]; // Extract 'en' from 'en-US'
          }
        }
      } else {
        // Mobile: the device's preferred language. This only fills the gap for a
        // first-time user — a saved choice (onboarding picker, settings, profile)
        // always wins in initializeLanguage. Languages the app does not ship fall
        // through to the mapping below and end up on English, as before.
        try {
          // Required lazily: a missing native module (tests, an old binary) must degrade to
          // English, never throw at import time and take the whole language store with it.
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const RNLocalize = require('react-native-localize');
          deviceLanguage = RNLocalize.getLocales?.()?.[0]?.languageCode || 'en';
        } catch {
          deviceLanguage = 'en';
        }
      }
      
      // Check if we support this language
      const normalizedDeviceLanguage = normalizeQuestionLocale(deviceLanguage);
      if (normalizedDeviceLanguage !== 'en' || deviceLanguage === 'en') {
        console.log(`🌍 Device language detected: ${deviceLanguage}`);
        return normalizedDeviceLanguage;
      }
      
      // Handle common language mappings
      const languageMappings: Record<string, string> = {
        'fil': 'tl', // Filipino -> Tagalog
        'zh': 'en',  // Chinese -> English (fallback)
        'ja': 'en',  // Japanese -> English (fallback)
        'ar': 'en',  // Arabic -> English (fallback)
        'hi': 'en',  // Hindi -> English (fallback)
      };
      
      if (languageMappings[deviceLanguage]) {
        console.log(`🔄 Language mapping: ${deviceLanguage} -> ${languageMappings[deviceLanguage]}`);
        return normalizeQuestionLocale(languageMappings[deviceLanguage]);
      }
      
    } catch (error) {
      console.warn('⚠️ Failed to detect device language:', error);
    }
    
    // Final fallback to English
    console.log('🔄 Using fallback language: en');
    return 'en';
  },

  initializeLanguage: async () => {
    try {
      console.log('🔄 Loading saved language from storage...');

      let savedLanguage = null;
      let isAuthenticated = false;

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        try {
          const params = new URLSearchParams(window.location.search);
          const screenshotLanguage = params.get('lang');
          if (screenshotLanguage) {
            savedLanguage = normalizeQuestionLocale(screenshotLanguage);
            await saveSetting('language', savedLanguage);
            localStorage.setItem('user_selected_language', savedLanguage);
            console.log(`🎯 Screenshot URL language: ${savedLanguage}`);
          }
        } catch (urlLanguageError) {
          console.warn('⚠️ Failed to read screenshot URL language:', urlLanguageError);
        }
      }

      // First check if user is authenticated and has a language preference in their profile
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data: { user } } = await supabase.auth.getUser();

        if (user && !savedLanguage) {
          isAuthenticated = true;
          console.log(`🔐 User authenticated: ${user.id}`);

          // For authenticated users, prioritize their profile language
          const { data } = await supabase
            .from("user_profiles")
            .select("language")
            .eq("id", user.id)
            .single();

          if (data && data.language) {
            savedLanguage = normalizeQuestionLocale(data.language);
            console.log(`✅ Loaded language from user profile: ${savedLanguage}`);

            // Save to local settings and clear any conflicting landing page selection
            await saveSetting('language', savedLanguage);

            // Clear the landing page selection to prevent conflicts
            if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
              const landingSelection = localStorage.getItem('user_selected_language');
              if (landingSelection && landingSelection !== savedLanguage) {
                console.log(`🔄 Clearing conflicting landing page selection: ${landingSelection}`);
                localStorage.removeItem('user_selected_language');
              }
            }
          }
        }
      } catch (profileError) {
        console.warn('⚠️ Failed to check authentication status:', profileError);
      }

      // For non-authenticated users or if profile has no language, check other sources
      if (!savedLanguage) {
        // Check for landing page selection (only for non-authenticated users)
        if (!isAuthenticated && Platform.OS === 'web' && typeof localStorage !== 'undefined') {
          const landingSelection = localStorage.getItem('user_selected_language');
          if (landingSelection) {
            savedLanguage = normalizeQuestionLocale(landingSelection);
            console.log(`🎯 Landing page language selection (guest): ${savedLanguage}`);
            // Also save to persistent settings for consistency
            await saveSetting('language', savedLanguage);
          }
        }

        // Try to load from local settings
        if (!savedLanguage) {
          const localLanguage = await loadSetting('language', null);
          savedLanguage = localLanguage ? normalizeQuestionLocale(localLanguage) : null;
          console.log(`📱 Local settings language: ${savedLanguage}`);
        }
      }

      // Use saved language, or detect device language for first-time users
      const finalLanguage = savedLanguage || get().detectDeviceLanguage();
      console.log(`🎯 Final language selection: ${finalLanguage}`);

      // Update the store with the loaded/default language
      set({ language: finalLanguage, loading: false });
    } catch (error) {
      console.error('❌ Failed to initialize language:', error);
      // If everything fails, default to English and stop loading
      set({ language: 'en', loading: false });
    }
  },
}));
